"use client";

import * as React from "react";
import Image from "next/image";
import { motion } from "motion/react";
import { ArrowDown, ArrowRight, Check, Palette } from "lucide-react";

import { Link } from "@/navigation";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useTranslations } from "next-intl";
import {
  type QrTemplatePreviewLocale,
  STORED_QR_TEMPLATE_PRESETS,
  getStoredQrTemplatePreviewImageUrl,
} from "@/lib/qr-template-registry";

interface PocketCardsShowcaseProps {
  locale: string;
  autoExpand?: boolean;
  squareCardPreviews?: boolean;
  copy?: Partial<{
    badge: string;
    title: string;
    body: string;
    subtitle: string;
    bottomNote: string;
  }>;
  expandedCardIds?: string[];
  stackCardIds?: string[];
  theme?: {
    sectionStyle?: React.CSSProperties;
    badgeStyle?: React.CSSProperties;
    titleStyle?: React.CSSProperties;
    bodyStyle?: React.CSSProperties;
    subtitleStyle?: React.CSSProperties;
    loginButtonClassName?: string;
    loginButtonStyle?: React.CSSProperties;
    loginButtonVariant?: React.ComponentProps<typeof Button>["variant"];
    expandButtonClassName?: string;
    expandButtonStyle?: React.CSSProperties;
  };
}

const supportedLocales = new Set(["en", "da", "no", "sv", "de"]);

const DEFAULT_EXPANDED_CARD_IDS = [
  "balloons-qr-card",
  "default-qr-card",
  "eucalyptus-qr-card",
  "flower-qr-card",
  "gatsby-qr-card",
] as const;

const DEFAULT_STACK_CARD_IDS = [
  "eucalyptus-qr-card",
  "default-qr-card",
  "balloons-qr-card",
] as const;

// Hovering the stack fans the cards out like a hand of cards.
const stackCardClasses = [
  "left-1/2 top-1/2 -translate-x-[65%] -translate-y-[55%] -rotate-6 can-hover:group-hover/stack:-translate-x-[98%] can-hover:group-hover/stack:-translate-y-[50%] can-hover:group-hover/stack:-rotate-[13deg]",
  "left-1/2 top-1/2 -translate-x-[50%] -translate-y-[50%] rotate-0 z-10 can-hover:group-hover/stack:-translate-y-[57%]",
  "left-1/2 top-1/2 -translate-x-[35%] -translate-y-[45%] rotate-6 can-hover:group-hover/stack:-translate-x-[2%] can-hover:group-hover/stack:-translate-y-[42%] can-hover:group-hover/stack:rotate-[12deg]",
] as const;

function getExpandedCardOrigin(index: number) {
  return {
    x: 220 - index * 52,
    y: -170 + Math.min(index, 4) * 8,
    rotate: -9 + index * 2.5,
  };
}

function resolveCardsByIds<
  T extends {
    id: string;
  },
>(cards: T[], ids: readonly string[]) {
  return ids
    .map((id) => cards.find((card) => card.id === id))
    .filter((card): card is T => Boolean(card));
}

export default function PocketCardsShowcase({
  locale,
  autoExpand = true,
  squareCardPreviews = false,
  copy,
  expandedCardIds,
  stackCardIds,
  theme,
}: PocketCardsShowcaseProps) {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const sectionRef = React.useRef<HTMLDivElement | null>(null);
  const hasAutoExpandedRef = React.useRef(false);
  const userToggledRef = React.useRef(false);
  const t = useTranslations("Index.QRTemplates");
  const tPrint = useTranslations("Home.Event.Templates.printYourself");

  const previewLocale: QrTemplatePreviewLocale = supportedLocales.has(locale)
    ? (locale as QrTemplatePreviewLocale)
    : "en";

  const allCards = React.useMemo(
    () =>
      STORED_QR_TEMPLATE_PRESETS.filter((preset) =>
        preset.locales.includes(previewLocale)
      ).map((preset) => ({
        id: preset.id,
        label: preset.name,
        src: getStoredQrTemplatePreviewImageUrl(preset, previewLocale),
        variants: preset.templateJson.config.variants.map((variant) => ({
          id: variant.id,
          name: variant.name,
          swatchStart: variant.swatchStart,
          swatchEnd: variant.swatchEnd,
          style: variant.style,
        })),
        hasTasks: true,
      })),
    [previewLocale]
  );

  const resolvedExpandedCardIds = React.useMemo(
    () =>
      expandedCardIds && expandedCardIds.length > 0
        ? expandedCardIds.slice(0, 6)
        : Array.from(DEFAULT_EXPANDED_CARD_IDS),
    [expandedCardIds]
  );

  const expandedCards = React.useMemo(
    () => resolveCardsByIds(allCards, resolvedExpandedCardIds.slice(0, 5)),
    [allCards, resolvedExpandedCardIds]
  );

  const mobileExpandedCards = React.useMemo(
    () => resolveCardsByIds(allCards, resolvedExpandedCardIds),
    [allCards, resolvedExpandedCardIds]
  );

  const stackCards = React.useMemo(() => {
    const ids =
      stackCardIds && stackCardIds.length > 0
        ? stackCardIds.slice(0, 3)
        : Array.from(DEFAULT_STACK_CARD_IDS);
    const resolved = resolveCardsByIds(allCards, ids);

    return resolved.length > 0 ? resolved : expandedCards.slice(0, 3);
  }, [allCards, expandedCards, stackCardIds]);

  const content = {
    badge: t("badge"),
    title: t("title"),
    body: t("body"),
    subtitle: t("subtitle"),
    bottomNote: t("moreNote"),
    ...copy,
  };

  React.useEffect(() => {
    const sectionElement = sectionRef.current;

    if (
      !autoExpand ||
      !sectionElement ||
      hasAutoExpandedRef.current ||
      userToggledRef.current
    ) {
      return;
    }

    const tryAutoExpand = (triggerRatio: number, entry?: IntersectionObserverEntry) => {
      if (hasAutoExpandedRef.current || userToggledRef.current) {
        return false;
      }

      const rect = entry?.boundingClientRect ?? sectionElement.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const triggerLine = viewportHeight * triggerRatio;
      const sectionHasEnteredViewport = rect.top <= triggerLine;
      const sectionStillVisible = rect.bottom >= viewportHeight * 0.2;

      if (!sectionHasEnteredViewport || !sectionStillVisible) {
        return false;
      }

      hasAutoExpandedRef.current = true;
      setIsExpanded(true);
      return true;
    };

    const isMobileViewport = () => window.innerWidth < 768;

    if (isMobileViewport()) {
      const handleViewportChange = () => {
        if (tryAutoExpand(0.11)) {
          window.removeEventListener("scroll", handleViewportChange);
          window.removeEventListener("resize", handleViewportChange);
        }
      };

      window.addEventListener("scroll", handleViewportChange, { passive: true });
      window.addEventListener("resize", handleViewportChange);
      handleViewportChange();

      return () => {
        window.removeEventListener("scroll", handleViewportChange);
        window.removeEventListener("resize", handleViewportChange);
      };
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) {
          return;
        }

        if (tryAutoExpand(0.68, entry)) {
          observer.disconnect();
        }
      },
      {
        root: null,
        rootMargin: "0px 0px -36% 0px",
        threshold: 0.68,
      }
    );

    observer.observe(sectionElement);

    return () => observer.disconnect();
  }, [autoExpand]);

  const handleExpandToggle = React.useCallback(() => {
    userToggledRef.current = true;
    setIsExpanded(!isExpanded);
  }, [isExpanded]);

  const renderExpandedCard = (
    card: (typeof expandedCards)[number],
    index: number,
    keySuffix?: string
  ) => {
    const origin = getExpandedCardOrigin(index);

    return (
      <motion.div
        key={`${card.id}${keySuffix ?? ""}`}
        initial={false}
        animate={
          isExpanded
            ? { opacity: 1, x: 0, y: 0, rotate: 0, scale: 1 }
            : {
                opacity: 0,
                x: origin.x,
                y: origin.y,
                rotate: origin.rotate,
                scale: 0.72,
              }
        }
        transition={{
          type: "spring",
          stiffness: 240,
          damping: 24,
          delay: isExpanded ? index * 0.03 : 0,
        }}
        className={cn(
          "flex h-full w-full max-w-[152px] flex-col sm:max-w-[168px] md:max-w-[176px]",
          squareCardPreviews
            ? "bg-transparent"
            : "rounded-2xl bg-white p-2 shadow-md",
        )}
      >
        <div
          className={cn(
            "overflow-hidden",
            squareCardPreviews ? "rounded-none" : "rounded-xl",
          )}
        >
          <Image
            alt={`${card.label} print-ready card`}
            className={cn(
              "h-auto w-full",
              squareCardPreviews ? "rounded-none" : "rounded-xl",
            )}
            height={960}
            // Below the fold on every page; the grid opens as it's scrolled to.
            loading="lazy"
            sizes="(min-width: 1024px) 176px, (min-width: 640px) 168px, 152px"
            src={card.src}
            width={640}
          />
        </div>
        <div className="mt-auto space-y-2 px-1 pb-1 pt-3">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-[10px] font-semibold uppercase tracking-[0.12em] text-[#7a6b5f]">
              {tPrint("colorVariants")}
            </span>
            <div className="flex flex-wrap gap-1.5">
              {card.variants.slice(0, 4).map((variant) => (
                <div
                  key={variant.id}
                  title={variant.name}
                  className="rounded-full border border-black/6 bg-[#f7f1ea]/70 p-1"
                >
                  <span
                    className="block h-3.5 w-3.5 rounded-full border border-black/10 shadow-[inset_0_1px_0_rgba(255,255,255,0.45)]"
                    style={{
                      background:
                        variant.style === "gradient"
                          ? `linear-gradient(135deg, ${variant.swatchStart}, ${variant.swatchEnd})`
                          : variant.swatchStart,
                    }}
                  />
                </div>
              ))}
            </div>
          </div>
          {card.hasTasks ? (
            <div className="flex items-end gap-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-[#7a6b5f]">
              <span>{tPrint("photoChallenges")}</span>
              <Check className="mb-px h-3.5 w-3.5 shrink-0 self-end text-[#5d8a62]" />
            </div>
          ) : null}
        </div>
      </motion.div>
    );
  };

  return (
    <div
      ref={sectionRef}
      className="w-full bg-muted/50 px-4 py-20"
      style={theme?.sectionStyle}
    >
      <div className="mx-auto max-w-screen-lg">
        <div className="grid grid-cols-1 items-center gap-12 md:grid-cols-2">
          <div className="flex flex-col gap-6">
            <div
              className="inline-flex items-center gap-2 self-start rounded-full bg-primary/10 px-4 py-1.5 text-sm font-medium text-primary"
              style={theme?.badgeStyle}
            >
              <Palette className="h-3.5 w-3.5" />
              {content.badge}
            </div>
            <h2 className="text-3xl font-bold md:text-4xl" style={theme?.titleStyle}>
              {content.title}
            </h2>
            <p className="text-lg leading-relaxed text-muted-foreground" style={theme?.bodyStyle}>
              {content.body}
            </p>
            <p className="text-sm text-muted-foreground" style={theme?.subtitleStyle}>
              {content.subtitle}
            </p>

            <div className="flex flex-col gap-3 sm:flex-row">
              <Button
                asChild
                variant={theme?.loginButtonVariant ?? "dark"}
                className={cn("w-full sm:w-fit", theme?.loginButtonClassName)}
                style={theme?.loginButtonStyle}
              >
                <Link href="/login">
                  <span className="flex items-center gap-2">
                    {t("loginButton")}
                    <ArrowRight className="h-4 w-4" />
                  </span>
                </Link>
              </Button>

              <Button
                variant="outline"
                className={cn("w-full sm:w-fit", theme?.expandButtonClassName)}
                style={theme?.expandButtonStyle}
                onClick={handleExpandToggle}
                aria-expanded={isExpanded}
                aria-controls="pocket-card-expanded"
              >
                <span className="flex items-center gap-2">
                  {isExpanded ? t("collapseButton") : t("expandButton")}
                  <motion.span
                    animate={{ rotate: isExpanded ? 180 : 0 }}
                    transition={{ duration: 0.24, ease: "easeOut" }}
                    className="inline-flex"
                  >
                    <ArrowDown className="h-4 w-4" />
                  </motion.span>
                </span>
              </Button>
            </div>
          </div>

          <div
            className={cn(
              "overflow-hidden transition-[max-height,opacity,padding] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] md:hidden",
              isExpanded ? "max-h-[2200px] opacity-100" : "max-h-0 opacity-0"
            )}
          >
            <div id="pocket-card-expanded-mobile" className="pt-2">
              <div className="grid grid-cols-2 justify-items-center gap-3">
                {mobileExpandedCards.map((card, index) =>
                  renderExpandedCard(card, index, "-mobile")
                )}
              </div>
              {content.bottomNote ? (
                <p
                  className="mx-auto mt-5 max-w-md text-center text-xs leading-relaxed text-muted-foreground"
                  style={theme?.subtitleStyle}
                >
                  {content.bottomNote}
                </p>
              ) : null}
            </div>
          </div>

          <div className="group/stack relative hidden min-h-[400px] items-center justify-center md:flex">
            {stackCards.map((card, index) => (
              <div
                key={card.id}
                className={cn(
                  "absolute w-40 overflow-hidden shadow-lg transition-transform duration-500 ease-[var(--ease-overshoot)] md:w-48",
                  squareCardPreviews ? "rounded-none" : "rounded-xl",
                  stackCardClasses[index],
                  index === 1 ? "shadow-xl" : ""
                )}
              >
                <Image
                  alt={`${card.label} pocket card`}
                  className="h-auto w-full"
                  height={960}
                  // Below the fold, and hidden on phones, where lazy images never load.
                  loading="lazy"
                  sizes="(min-width: 768px) 192px, 160px"
                  src={card.src}
                  width={640}
                />
              </div>
            ))}
          </div>
        </div>

        <div
          className={cn(
            "hidden overflow-hidden transition-[max-height,opacity,padding] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] md:block",
            isExpanded ? "max-h-[1100px] opacity-100" : "max-h-0 opacity-0"
          )}
        >
          <div id="pocket-card-expanded" className="pt-8">
            <div className="grid grid-cols-5 justify-items-center gap-3 md:gap-4">
              {expandedCards.map((card, index) => renderExpandedCard(card, index))}
            </div>
            {content.bottomNote ? (
              <p
                className="mx-auto mt-5 max-w-xl text-center text-sm leading-relaxed text-muted-foreground"
                style={theme?.subtitleStyle}
              >
                {content.bottomNote}
              </p>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
